"use client";

import { BOOKING_LANGUAGE_COOKIE, BOOKING_LANGUAGE_COOKIE_MAX_AGE } from "@calcom/lib/bookingLanguages";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo } from "react";

type BookingLanguageContextValue = {
  languages: string[];
  locale: string;
};

const BookingLanguageContext = createContext<BookingLanguageContextValue | null>(null);

/** The booking languages and the page's current one, or null when booking languages are not configured. */
export const useBookingLanguage = () => useContext(BookingLanguageContext);

export function rememberBookingLanguage(locale: string) {
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${BOOKING_LANGUAGE_COOKIE}=${encodeURIComponent(locale)}; path=/; max-age=${BOOKING_LANGUAGE_COOKIE_MAX_AGE}; samesite=lax${secure}`;
}

export function BookingLanguageProvider({
  languages,
  locale,
  remember,
  children,
}: BookingLanguageContextValue & {
  remember: boolean;
  children: ReactNode;
}) {
  useEffect(() => {
    if (remember) rememberBookingLanguage(locale);
  }, [remember, locale]);

  const value = useMemo(() => ({ languages, locale }), [languages, locale]);

  return <BookingLanguageContext.Provider value={value}>{children}</BookingLanguageContext.Provider>;
}
